/**
 * EditorialMastheadCover — the editorial title page for the Editorial-Feature
 * Monograph direction (Part L). A more DRAMATIC sibling of MastheadCover: a
 * larger serif display title leads, the SITE NAME reads as the byline, a
 * standfirst-style abstract (the persisted exec-summary, rendered in full)
 * sits below the title, then the on-cover 2-column numbered TOC (dotted leaders
 * + right-aligned page numbers), then the centred brand logo, and finally the
 * centred bottom-pinned Confidential/Beta disclaimer footer.
 *
 * RELATION TO MastheadCover: the data contract (`CoverProps`), the
 * carriesToc/tocEntries/tocPageNumbers TOC block (MastheadCover DEFECT 8) and
 * the mt-auto/text-center disclaimer footer (MastheadCover DEFECT 9) are reused
 * VERBATIM in language so the two covers stay in lock-step. What differs is the
 * editorial register: a bigger, more confident serif title (display font, a
 * larger type scale than the masthead's `× 1.67`), a "standfirst" run-in
 * abstract that reads like a feature lede, and a centred brand logo below the
 * contents (the masthead carries neither).
 *
 * ADDITIVE / FLAG-GATED: this cover is NEVER reached unless a caller — gated by
 * the editorial-layouts flag (prod-default OFF) — dispatches to it. With the
 * flag OFF every existing cover/preset/visual-matrix cell renders byte-for-byte
 * as before, because nothing references this file. The editorial treatment
 * appears ONLY when the flag is ON.
 *
 * Theme-aware + null-safe: title/byline/abstract/KPIs all read EXISTING
 * persisted report+theme data via the shared journal/KPI resolvers; an empty
 * summary collapses the standfirst rather than rendering a stray "—", a near-
 * empty index collapses the TOC (the <3-entry guard), and a legacy report with
 * no resolved branding still renders the centred platform wordmark rather than
 * throwing. Stays on OUR tokens — roleColor roles,
 * displayFont/headingFont/bodyFont, white paper.
 */
import React from 'react';
import { CitedReportProse } from '../CitedReportProse';
import { walkClaimsText } from '../../../../lib/walkClaimsText';
import {
  resolveCoverTitle,
  resolveSiteWordmark,
  resolveBylineParts,
  resolveAbstractFull,
  truncateCoverStandfirst,
} from '../../../../lib/reportRendering/journalCoverData';
import { CoverCustomerLogo, customerLogoImgClass } from './CoverCustomerLogo';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX } from '../../../../lib/reportCoverTokens';
import { REPORT_SIDE_GUTTER_PX } from '../../../../lib/reportSpacingTokens';
import { formatPageNumber, normalizePageNumberFormat } from '../../../../lib/reportLayoutTokens';
import { resolveEditorialLayer, hasGeneratedEditorialLayer } from '../../../../lib/editorial/resolveEditorialLayer';
import { resolveCoverVerdict, type VerdictTier } from '../../../../lib/reportVerdict';
import { REPORT_COVER_DISCLAIMER } from '../../../../lib/reportLegalDisclaimer';
import type { CoverProps } from './types';
import { CoverHeaderPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

/**
 * DEFECT — doubled "Confidential. Beta workflow…" disclaimer. The resolved
 * `disclaimer` prop defaults to {@link REPORT_COVER_DISCLAIMER} (the EXACT beta
 * caveat) when no archetype-specific override exists, and the footer ALSO
 * hard-codes that same caveat below it — so the line rendered TWICE on the
 * default cover. We only render the prop block when it carries a DISTINCT
 * archetype override (trimmed, non-empty, and not the canonical beta line);
 * the hard-coded caveat below is always the single source of the beta text.
 */
function isDistinctDisclaimer(disclaimer: string | undefined): disclaimer is string {
  if (typeof disclaimer !== 'string') return false;
  const trimmed = disclaimer.trim();
  return trimmed.length > 0 && trimmed !== REPORT_COVER_DISCLAIMER.trim();
}

/** Map a verdict tier to a role-colour key for the outlined cover stamp. */
function verdictStampRole(tier: VerdictTier): 'positive' | 'warning' | 'negative' | 'muted' {
  switch (tier) {
    case 'green': return 'positive';
    case 'amber': return 'warning';
    case 'red': return 'negative';
    default: return 'muted';
  }
}

type CoverTocDensity = 'standard' | 'compact' | 'compressed';

/**
 * Approximate how many rendered lines a TOC label needs in one cover column.
 * This is deliberately conservative: it is used only to balance the two
 * explicit block columns and select typography density; the browser remains
 * free to wrap the complete label normally.
 */
function estimateCoverTocLines(label: string): number {
  const normalized = label.replace(/\s+/g, ' ').trim();
  return Math.max(1, Math.ceil(normalized.length / 34));
}

/** Keep column-major reading order while balancing wrapped-label height. */
function resolveCoverTocSplit(entries: readonly { label: string }[]): number {
  if (entries.length <= 1) return entries.length;
  const lineUnits = entries.map((entry) => estimateCoverTocLines(entry.label));
  const total = lineUnits.reduce((sum, units) => sum + units, 0);
  let running = 0;
  let bestSplit = 1;
  let bestDelta = Number.POSITIVE_INFINITY;

  for (let index = 0; index < lineUnits.length - 1; index += 1) {
    running += lineUnits[index]!;
    const delta = Math.abs(total - (running * 2));
    // Prefer the later split on a tie. Equal-height labels therefore retain
    // the established ceil(n / 2) split used by the 17-entry Redhead cover.
    if (delta <= bestDelta) {
      bestDelta = delta;
      bestSplit = index + 1;
    }
  }

  return bestSplit;
}

function completeSentenceCoverStandfirst(
  raw: string | null | undefined,
  maxChars = 480,
): string | null {
  const oneLine = raw?.replace(/\s+/g, ' ').trim() ?? '';
  if (!oneLine) return null;
  if (oneLine.length <= maxChars) return oneLine;
  const sentenceEndings: number[] = [];
  const re = /(?<!\d)[.!?](?=\s|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(oneLine)) !== null) {
    const endIdx = m.index + 1;
    const prefix = oneLine.slice(Math.max(0, m.index - 6), m.index + 1);
    if (/\b(?:llc|inc|ltd|corp|co|vs|etc|fig|ref|sec|no|vol|approx|est)\.$/i.test(prefix)) {
      continue;
    }
    sentenceEndings.push(endIdx);
  }
  const withinMax = sentenceEndings.filter((idx) => idx >= 80 && idx <= maxChars);
  if (withinMax.length > 0) {
    return oneLine.slice(0, withinMax[withinMax.length - 1]!).trim();
  }
  const firstSentenceEnd = sentenceEndings.find((idx) => idx >= 80 && idx <= 950);
  if (firstSentenceEnd !== undefined) {
    return oneLine.slice(0, firstSentenceEnd).trim();
  }
  return truncateCoverStandfirst(oneLine, maxChars);
}

export const EditorialMastheadCover: React.FC<CoverProps> = ({
  report, theme, branding,
  disclaimer,
  tocEntries,
  tocPageNumbers,
  tocPageNumberFormat,
  boundedStandfirst = false,
}) => {
  // BRANDING: the house editorial title page surfaces the brand identity as a
  // CENTRED logo below the on-cover contents (the house cover request) — the
  // primary brand mark, plus the customer logo in co-brand ('customer' audience)
  // mode. `effectiveBranding` falls back to the platform-default primary mark so
  // a report with no resolved branding still renders a centred wordmark.
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };

  const title = resolveCoverTitle(report);
  const wordmark = resolveSiteWordmark(report, branding);
  // The byline IS the SITE NAME (COVER-CONTENT BATCH parity with the masthead);
  // the org identity stays in the series eyebrow above.
  const author = wordmark.toUpperCase();
  const affiliation = resolveBylineParts(report, branding).join(' · ');
  // The cover owns only a bounded standfirst. The complete Executive Summary
  // remains in the canonical body and is never suppressed by cover choice.
  //
  // WAVE 3 — when a GENERATED editorial layer exists for the 'cover' pseudo-
  // section, the standfirst becomes the grounding-validated editorial narrative
  // (the monograph lede). Absent (flag OFF / legacy) it stays resolveAbstractFull
  // VERBATIM, so the flag-OFF cover is byte-identical to today.
  const generatedAbstract = resolveEditorialLayer(report, 'cover').editorialNarrative;
  const abstract = boundedStandfirst
    ? hasGeneratedEditorialLayer(report, 'cover')
      ? completeSentenceCoverStandfirst(generatedAbstract ?? resolveAbstractFull(report))
      : completeSentenceCoverStandfirst(resolveAbstractFull(report))
    : hasGeneratedEditorialLayer(report, 'cover')
      ? generatedAbstract ?? resolveAbstractFull(report)
      : resolveAbstractFull(report);

  // WAVE 3 — outlined VERDICT STAMP (option-C masthead). Deterministic: the
  // canonical COVER verdict + the persisted conclusion conditions count.
  // Rendered only when there is a real verdict (tier !== 'none') so legacy /
  // unscored reports show no stray stamp (byte-stable). For the executive
  // summary the stamp sources the gen-time decision-rollup band (PG-3 SSOT) so
  // the exec cover finally carries its GO/NO-GO; every other type delegates to
  // getDisplayVerdict unchanged. NB this is deterministic and shows on the
  // editorial cover regardless of the narrative layer — it is chrome, not
  // generated prose.
  const verdict = resolveCoverVerdict(report);
  const conditionsCount = report.conclusion?.keyConditions?.filter(
    (c) => typeof c === 'string' && c.trim().length > 0,
  ).length ?? 0;
  const showVerdictStamp = (verdict.tier !== 'none' || verdict.savedDecisionPending) && verdict.label !== '—';
  const verdictRole = verdictStampRole(verdict.tier);
  const hasDistinctDisclaimer = isDistinctDisclaimer(disclaimer);

  // Body / affiliation copy size derives from the resolved body size
  // (headingScale + themeTypographyScale aware) × the theme's bodyTextScale —
  // same density-tracking as the masthead and the rest of the report.
  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;

  // DEFECT 8 — compact 2-COLUMN numbered index threaded from the caller
  // (deriveTocEntries + exec-summary de-dup already applied). Mirrors the
  // masthead's <3-entry guard so a near-empty index never renders.
  //
  // The caller always threads the FULL index for an eligible carrying cover.
  // This >=3 guard is only the near-empty floor; there is deliberately no upper
  // bound because Contents is part of page 1 for every report.
  const carriesToc = Array.isArray(tocEntries) && tocEntries.length >= 3;
  const tocFmt = normalizePageNumberFormat(tocPageNumberFormat);

  // DEFECT F1 — render the on-cover index as an EXPLICIT two-column grid split
  // instead of a CSS `column-count: 2` multi-column block. The deck clamps the
  // outer `[data-section="cover"]` box to ONE page with `overflow: hidden` +
  // `break-inside: avoid` (PaginatedReportView "COVER MUST FIT ONE PAGE"), and
  // inside that clamped, non-fragmenting box Paged.js MIS-FRAGMENTS a CSS
  // multi-column container and DROPS most of its rows — the cover printed
  // "N sections" yet listed only the first ~2 entries, then 55-67% of the page
  // went blank (confirmed on supply-chain-risk · environmental · public-
  // sentiment · feasibility). A two-track grid of BLOCK columns is laid out
  // once and never fragmented, so every entry renders with its dotted leader +
  // page number. The split is chosen from conservative wrapped-line estimates,
  // rather than item count alone, so one column cannot become much taller just
  // because it contains longer labels. Reading order remains column-major and
  // the GLOBAL row index drives the printed numeral.
  const tocSplit = carriesToc ? resolveCoverTocSplit(tocEntries!) : 0;
  const leftTocFlowUnits = carriesToc
    ? tocEntries!.slice(0, tocSplit).reduce((sum, entry) => sum + estimateCoverTocLines(entry.label), 0)
    : 0;
  const rightTocFlowUnits = carriesToc
    ? tocEntries!.slice(tocSplit).reduce((sum, entry) => sum + estimateCoverTocLines(entry.label), 0)
    : 0;
  const maxTocColumnFlowUnits = Math.max(leftTocFlowUnits, rightTocFlowUnits);
  const tocDensity: CoverTocDensity = carriesToc && (
    tocEntries!.length >= 24 || maxTocColumnFlowUnits >= 28 || (Boolean(abstract && abstract.length > 360) && tocEntries!.length >= 15)
  )
    ? 'compressed'
    : carriesToc && (tocEntries!.length >= 12 || maxTocColumnFlowUnits >= 16)
      ? 'compact'
      : 'standard';
  const denseCoverIndex = tocDensity !== 'standard';
  const compressedCoverIndex = tocDensity === 'compressed';
  // The disclaimer remains bottom-pinned, but this padding makes its footprint
  // part of normal layout accounting. Distinct customer copy may wrap to an
  // additional line, so reserve a larger footer band for that case.
  const footerReservePx = hasDistinctDisclaimer ? 82 : 64;
  const renderTocRow = (entry: NonNullable<typeof tocEntries>[number], i: number) => {
    const page = tocPageNumbers?.[entry.id];
    const hasPage = typeof page === 'number' && Number.isFinite(page);
    return (
      <div
        key={entry.id}
        data-testid={`cover-toc-entry-${entry.id}`}
        data-cover-toc-row="true"
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 6,
          padding: compressedCoverIndex ? '1px 0' : denseCoverIndex ? '2.5px 0' : '3.5px 0',
          breakInside: 'avoid',
          pageBreakInside: 'avoid',
        }}
      >
        {/* numeral — apparatus small-caps, wide-tracked, accent ink. */}
        <span
          className="tabular-nums"
          style={{
            fontFamily: theme.apparatusFont ?? theme.bodyFont,
            fontSize: '11px',
            fontWeight: 600,
            width: compressedCoverIndex ? 18 : 22,
            flexShrink: 0,
            color: roleColor(theme, 'accent'),
          }}
        >
          {String(i + 1).padStart(2, '0')}
        </span>
        {/* title — reading serif, soft ink. */}
        <span
          style={{
            fontFamily: theme.bodyFont,
            fontSize: '11px',
            lineHeight: compressedCoverIndex ? 1.08 : 1.2,
            color: roleColor(theme, 'body'),
            flex: '0 1 auto',
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          <a
            href={`#${entry.id}`}
            style={{
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {entry.label}
          </a>
        </span>
        {/* dotted leader — fills the row; nudged onto the baseline. */}
        <span
          aria-hidden="true"
          style={{
            flex: 1,
            minWidth: compressedCoverIndex ? 6 : 10,
            borderBottom: `1px dotted ${roleColorAlpha(theme, 'ink', 0.3)}`,
            transform: 'translateY(-3px)',
          }}
        />
        {/* page number — apparatus, muted, right-aligned. */}
        {hasPage && (
          <span
            className="tabular-nums"
            data-testid={`cover-toc-page-${entry.id}`}
            style={{
              fontFamily: theme.apparatusFont ?? theme.bodyFont,
              fontSize: '11px',
              fontWeight: 500,
              flexShrink: 0,
              color: roleColor(theme, 'muted'),
            }}
          >
            {formatPageNumber(page!, tocFmt)}
          </span>
        )}
      </div>
    );
  };

  return (
    <div
      className="relative"
      data-testid="cover-editorial-masthead"
      data-cover-variant="editorial-masthead"
      data-cover-footer-reserve-px={footerReservePx}
      data-cover-toc-density={carriesToc ? tocDensity : undefined}
      style={{
        color: roleColor(theme, 'body'),
        boxSizing: 'border-box',
        paddingBottom: footerReservePx,
        // Stretch the root to the cover's INNER content box. The outer cover
        // shell already contributes REPORT_SIDE_GUTTER_PX on both vertical
        // edges; counting the full printable height again here makes Paged.js
        // see `shell padding + child height > page`, fragment the flex child at
        // the TOC list, and then clip that continuation behind the cover's
        // one-page overflow guard. Subtracting the two shell gutters preserves
        // the authored top/bottom whitespace while keeping the complete
        // two-column index, logo and disclaimer in the first-page fragment.
        // The 520px floor remains for the continuous viewer. The root stays a
        // plain block because Paged.js 0.4.x can truncate the remaining
        // siblings when it fragments a nested flex column; the disclaimer is
        // positioned against this relative box instead of relying on mt-auto.
        //
        // DEFECT 1 (blank orphan page) — trim by the SAME 1mm epsilon the paged
        // deck applies to the OUTER `#cover` clamp (PaginatedReportView). The
        // outer box is clamped to `page - 14mm - 16mm - 1mm`; matching the inner
        // min-height to that (not 1mm taller) guarantees the bottom-pinned
        // disclaimer never tips the cover OVER its clamped box — which had pushed
        // the disclaimer tail onto a near-empty page-2 between the cover and §01.
        minHeight: `max(${COVER_MIN_HEIGHT_PX}px, calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm - ${REPORT_SIDE_GUTTER_PX * 2}px))`,
      }}
    >
      {/* Series eyebrow. */}
      <div
        className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] pb-2.5"
        style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, borderBottom: `1px solid ${roleColor(theme, 'rule')}`, opacity: 0.95 }}
        data-testid="cover-editorial-masthead-series"
      >
        <span />
        <span data-testid="cover-confidential-tag">Confidential</span>
      </div>
      {/* GATEWAY POWER platform credit — right-aligned under the series rule,
          in the title block's top padding (pt-5 / pt-8 / pt-12 by TOC density;
          zero height, so footerReservePx and the one-page fit are untouched). */}
      <CoverHeaderPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        fontFamily={theme.bodyFont}
        testId="cover-editorial-masthead-platform-credit"
      />

      {/* Dramatic editorial title + SITE NAME byline + affiliation. */}
      <div className={`px-1 ${compressedCoverIndex ? 'pt-5 pb-3' : denseCoverIndex ? 'pt-8 pb-4' : 'pt-12 pb-6'}`}>
        <h1
          id="cover-title"
          // EDITORIAL type scale: a larger, more confident serif display title
          // than the masthead (which tops out at h2 × 1.67). Here the band
          // floor/ceiling open up — clamp(34px, 6vw, h2 × 2.6) — so the cover
          // reads as a feature monograph headline, not a journal masthead.
          // Display font falls back to headingFont; tighter leading for the
          // bigger size.
          style={{
            fontFamily: theme.displayFont || theme.headingFont,
            color: reportHeadingInk(theme),
            fontSize: `clamp(34px, 6vw, calc(${reportTypographySizes(theme).h2} * 2.6))`,
            fontWeight: 500,
            maxWidth: '20ch',
            lineHeight: 1.05,
            letterSpacing: '-0.01em',
          }}
          data-testid="cover-editorial-masthead-title"
        >
          {title}
        </h1>
        <div
          className={`text-[11px] uppercase tracking-[0.16em] ${compressedCoverIndex ? 'mt-3' : denseCoverIndex ? 'mt-4' : 'mt-6'}`}
          style={{ color: roleColor(theme, 'body'), fontFamily: theme.bodyFont, opacity: 0.8 }}
          data-testid="cover-editorial-masthead-author"
          data-cover-site-name-header="true"
        >
          <span data-testid="cover-site-name-header">{author}</span>
        </div>
        {affiliation && (
          <div
            className="italic mt-1"
            style={{ color: roleColor(theme, 'body'), fontFamily: theme.headingFont, opacity: 0.8, fontSize: bodyFontSize }}
            data-testid="cover-editorial-masthead-affiliation"
          >
            {affiliation}
          </div>
        )}
      </div>

      {/* Standfirst abstract — the cover-carried exec summary as a feature lede.
          A larger, drop-cap-style run-in accent label opens the paragraph
          (editorial standfirst convention); the body is the persisted summary
          rendered IN FULL. roleColor is null-safe; an empty summary collapses
          the whole block rather than rendering a stray run-in label. */}
      {abstract && (
        <div
          className={`px-1 ${compressedCoverIndex ? 'pt-0 pb-3' : denseCoverIndex ? 'pt-1 pb-5' : 'pt-2 pb-7'}`}
          style={{ borderTop: `1px solid ${roleColorAlpha(theme, 'ink', 0.33)}` }}
        >
          <div
            className={`text-left ${compressedCoverIndex ? 'mt-2 leading-snug' : denseCoverIndex ? 'mt-3 leading-relaxed' : 'mt-5 leading-relaxed'}`}
            style={{
              color: roleColor(theme, 'body'),
              fontFamily: theme.bodyFont,
              hyphens: 'auto',
              // Standfirst reads one step larger than body for the lede voice.
              fontSize: compressedCoverIndex
                ? `calc(${bodyFontSize} * 0.96)`
                : `calc(${bodyFontSize} * 1.12)`,
            }}
            data-testid="cover-editorial-masthead-abstract"
          >
            <span
              className="text-[11px] font-bold uppercase tracking-[0.16em] mr-2 float-left"
              // Run-in label takes the brand accent ink (editorial standfirst
              // convention) instead of the heading ink. roleColor is null-safe.
              style={{ color: roleColor(theme, 'accent'), fontFamily: theme.apparatusFont ?? theme.bodyFont }}
            >
              In brief
            </span>
            {/* CITE-PILL-1 — the IN BRIEF lede rendered literal "[Ref N]";
                route through the claims walker so cover citations are the
                same hyperlinked superscript pills as the body. */}
            {report.reportType === 'public-sentiment'
              ? <CitedReportProse text={abstract} references={report.references}
                  renderText={text => walkClaimsText(text, report.references, report.claims, null, { suppressClaimMarkers: true })} />
              : walkClaimsText(abstract, report.references, report.claims, null, { suppressClaimMarkers: true })}
          </div>
        </div>
      )}

      {/* WAVE 3 — outlined VERDICT STAMP (option-C masthead). An outlined pill
          (no fill) carrying a tier-coloured dot + the deterministic verdict
          word + a mono sub-line naming the gating-conditions count. Rendered
          only when a real verdict exists; legacy / unscored covers show none. */}
      {showVerdictStamp && (
        <div className={`px-1 ${compressedCoverIndex ? 'pb-2' : denseCoverIndex ? 'pb-4' : 'pb-6'}`} data-testid="cover-editorial-masthead-verdict">
          <span
            className="inline-flex items-center gap-2.5 rounded-sm"
            style={{
              border: `1.5px solid ${roleColor(theme, verdictRole)}`,
              padding: compressedCoverIndex ? '5px 11px' : '8px 16px',
            }}
            data-testid="cover-editorial-masthead-verdict-stamp"
            data-verdict-tier={verdict.tier}
          >
            <span
              aria-hidden="true"
              style={{ width: 9, height: 9, borderRadius: '9999px', background: roleColor(theme, verdictRole) }}
            />
            <span
              className="text-[12px] font-bold uppercase tracking-[0.2em]"
              style={{ color: roleColor(theme, verdictRole), fontFamily: theme.bodyFont }}
              data-testid="cover-editorial-masthead-verdict-label"
            >
              {verdict.label}
            </span>
            {conditionsCount > 0 && (
              <span
                className="text-[10px] uppercase tracking-[0.1em] pl-2.5"
                style={{
                  color: roleColor(theme, 'muted'),
                  fontFamily: theme.bodyFont,
                  borderLeft: `1px solid ${roleColor(theme, 'rule')}`,
                }}
                data-testid="cover-editorial-masthead-verdict-conditions"
              >
                {conditionsCount} gating {conditionsCount === 1 ? 'condition' : 'conditions'}
              </span>
            )}
          </span>
        </div>
      )}

      {/* On-cover CONTENTS — a compact 2-COLUMN numbered list with dotted
          leaders and right-aligned page numbers (the house TOC). The full
          index stays here for every eligible report and replaces the
          standalone page (the caller suppresses it via `coverCarriesToc`) and
          consumes the REAL `deriveTocEntries` output + the MEASURED `pageMap`
          page numbers the caller threads in. Numerals + page numbers take the
          APPARATUS (grotesque) face; titles take the reading serif. Pure layout;
          entries/page-numbers are caller-computed. Inks via roleColor(Alpha), so
          the list takes on the active preset's ink/accent. */}
      {carriesToc && (
        <div
          className={`px-1 ${compressedCoverIndex ? 'pt-2 pb-1' : denseCoverIndex ? 'pt-3 pb-2' : 'pt-5 pb-2'}`}
          data-testid="cover-editorial-masthead-toc"
          data-cover-toc-flow="normal-block"
          data-cover-toc-density={tocDensity}
          data-cover-toc-max-column-flow-units={maxTocColumnFlowUnits}
          style={{
            // Preserve the established 17-entry Redhead cover rhythm without
            // imposing a ceiling. Normal-flow content can grow past this floor
            // when labels wrap, so neither the list nor the following logo can
            // overlap it.
            minHeight: compressedCoverIndex ? undefined : denseCoverIndex ? 215 : 245,
          }}
        >
          <div
            className={`flex items-baseline justify-between font-bold uppercase tracking-[0.18em] ${compressedCoverIndex ? 'mb-1' : denseCoverIndex ? 'mb-2' : 'mb-3'}`}
            style={{
              color: roleColor(theme, 'muted'),
              fontFamily: theme.apparatusFont ?? theme.bodyFont,
              fontSize: compressedCoverIndex ? '9px' : '10px',
            }}
          >
            <span>Contents</span>
            <span
              className="font-medium tracking-[0.14em]"
              style={{ color: roleColorAlpha(theme, 'ink', 0.45), fontFamily: theme.apparatusFont ?? theme.bodyFont }}
              data-testid="cover-editorial-masthead-toc-count"
            >
              {tocEntries!.length} sections
            </span>
          </div>
          {/* TWO-COLUMN INDEX (DEFECT F1) — an EXPLICIT grid split, NOT a CSS
              `column-count` multi-column block: Paged.js drops multicol rows
              inside the clamped, overflow-hidden, break-inside:avoid cover box
              (see `renderTocRow`). The left column carries a leading contiguous
              run and the right carries the remainder, preserving conventional
              column-major reading order while the estimated wrapped heights
              stay balanced. `minWidth: 0` lets each row's dotted leader flex
              without forcing horizontal overflow. */}
          <div
            data-testid="cover-editorial-masthead-toc-list"
            data-cover-layout-region="toc-grid"
            data-cover-toc-columns="2"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              columnGap: compressedCoverIndex ? 20 : denseCoverIndex ? 32 : 40,
              alignItems: 'start',
              width: '100%',
            }}
          >
            <div
              data-cover-toc-column="1"
              data-cover-toc-flow-units={leftTocFlowUnits}
              style={{ minWidth: 0 }}
            >
              {tocEntries!.slice(0, tocSplit).map((entry, j) => renderTocRow(entry, j))}
            </div>
            <div
              data-cover-toc-column="2"
              data-cover-toc-flow-units={rightTocFlowUnits}
              style={{ minWidth: 0 }}
            >
              {tocEntries!.slice(tocSplit).map((entry, j) => renderTocRow(entry, tocSplit + j))}
            </div>
          </div>
        </div>
      )}

      {/* CENTRED BRAND LOGO — below the contents (the house cover request). The
          primary brand mark, plus the customer logo in co-brand ('customer'
          audience) mode with a hairline divider. The house cover is white
          (on-light), so logos render at true colour, unchipped. Null-safe: the
          block renders only when a logo URL resolves (the platform-default
          primary mark always does). */}
      {(effectiveBranding.primaryLogoUrl || effectiveBranding.secondaryLogoUrl) && (
        <div
          className={`px-1 ${compressedCoverIndex ? 'pt-2 pb-1' : denseCoverIndex ? 'pt-3 pb-1' : 'pt-6 pb-2'} flex items-center justify-center gap-4`}
          data-testid="cover-editorial-masthead-logo"
          data-cover-layout-region="logo"
          data-audience-mode={effectiveBranding.audienceMode}
        >
          {effectiveBranding.primaryLogoUrl && (
            <CoverCustomerLogo
              src={effectiveBranding.primaryLogoUrl}
              alt={effectiveBranding.primaryLogoAlt}
              tone="on-light"
              className={customerLogoImgClass('on-light', `${compressedCoverIndex ? 'h-7' : denseCoverIndex ? 'h-8' : 'h-9'} max-w-[180px]`)}
              data-testid="cover-editorial-masthead-logo-primary"
            />
          )}
          {effectiveBranding.audienceMode === 'customer' && effectiveBranding.secondaryLogoUrl && (
            <>
              <span
                className="h-6 w-px"
                style={{ backgroundColor: roleColor(theme, 'rule') }}
                aria-hidden="true"
              />
              <CoverCustomerLogo
                src={effectiveBranding.secondaryLogoUrl}
                alt={effectiveBranding.secondaryLogoAlt}
                tone="on-light"
                className={customerLogoImgClass('on-light', `${compressedCoverIndex ? 'h-7' : denseCoverIndex ? 'h-8' : 'h-9'} max-w-[180px]`)}
                data-testid="cover-editorial-masthead-logo-secondary"
              />
            </>
          )}
        </div>
      )}

      {/* DEFECT 9 — CONFIDENTIAL DISCLAIMER footer: a centred, muted, bottom-
          PINNED line. Absolute positioning keeps it against the inner A4 edge
          without making the cover a flex fragmentation context; matching root
          padding reserves its full footprint from the normal-flow TOC/logo.
          A hairline top rule separates it from the index above. */}
      <div
        className="absolute bottom-0 left-0 right-0 px-1 pt-3 pb-2 text-center"
        style={{ borderTop: `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}` }}
        data-testid="cover-editorial-masthead-disclaimer"
        data-cover-layout-region="footer"
        data-cover-footer-reserved-by="root-padding"
        data-cover-footer-reserve-px={footerReservePx}
      >
        {hasDistinctDisclaimer && (
          <div
            className="text-[8px] leading-relaxed"
            style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          >
            {disclaimer}
          </div>
        )}
        <div
          className="text-[8px] leading-relaxed mt-1 mx-auto max-w-xl"
          style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          data-testid="cover-editorial-masthead-beta-disclaimer"
        >
          Confidential. Beta workflow: directional guidance only; may contain errors.
          Independently validate all findings with qualified advisors before any decision.
        </div>
      </div>
    </div>
  );
};
